import { useTranslation } from 'react-i18next';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Spinner } from '@/components/ui/spinner';

interface LogoutDialogProps {
  email: string | null;
  isPending?: boolean;
  onConfirm: () => void | Promise<void>;
  onOpenChange: (open: boolean) => void;
  open: boolean;
}

export function LogoutDialog({
  email,
  isPending = false,
  onConfirm,
  onOpenChange,
  open,
}: LogoutDialogProps) {
  const { t } = useTranslation();
  const handleConfirm = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (isPending) {
      return;
    }

    await onConfirm();
    onOpenChange(false);
  };

  return (
    <AlertDialog onOpenChange={onOpenChange} open={open}>
      <AlertDialogContent className="max-w-[calc(100%-2rem)] gap-2.5 p-3">
        <AlertDialogHeader className="gap-0.5">
          <AlertDialogTitle className="text-secondary text-left font-['Inter'] text-[13px] leading-[170%] font-medium">
            {t('profile.logout.dialog.title')}
          </AlertDialogTitle>
          <AlertDialogDescription className="text-tertiary mt-0.5 text-left font-['Inter'] text-[12px] leading-[160%] font-normal">
            {t('profile.logout.dialog.description', {
              email: email ?? t('profile.logout.dialog.fallbackEmail'),
            })}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="mt-1.5 flex-row justify-end gap-1.5">
          <AlertDialogCancel
            className="border-secondary text-secondary hover:bg-back-bg2 bg-popover m-0 h-7 rounded-md border px-2.5 py-1.5 text-xs"
            disabled={isPending}
            onClick={() => onOpenChange(false)}
            size="sm"
            variant="outline"
          >
            {t('profile.logout.dialog.cancel')}
          </AlertDialogCancel>
          <AlertDialogAction
            className="bg-primary text-primary-foreground hover:bg-primary m-0 inline-flex h-7 items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs hover:opacity-90"
            disabled={isPending}
            onClick={handleConfirm}
          >
            {isPending && <Spinner className="text-primary-foreground size-3" />}
            {isPending ? t('profile.logout.dialog.loggingOut') : t('profile.logout.dialog.confirm')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
